package org.multiplatform.kotfolio.pages.kmm

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.core.Page

@Page("/kmm/formz_validation")
@Composable
fun KMPValidationFormZPage() {
    BlogLayout(
        PageProperties(
            "FromZ Validation",
            "Formz aims to simplify form representation and validation in a generic way."
        ),
        BlogProperties("Kotlin Multiplatform", "FromZ Validation")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            KotlinCode(
                code = """
                    implementation("io.github.zainulhassan815:formz:1.0.1")
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Email Validation
                    enum class EmailError {
                        Empty,
                        Invalid
                    }

                    class Email(
                        value: String,
                        isPristine: Boolean = true
                    ) : FormInput<String, EmailError>(value, isPristine) {
                        override fun validator(value: String): EmailError? = when {
                            value.isBlank() -> EmailError.Empty
                            !PatternsCompat.EMAIL_ADDRESS.matcher(value).matches() -> EmailError.Invalid
                            else -> null
                        }
                    }
                    
                    //Password Validation
                    enum class PasswordError {
                        Empty,
                        TooShort
                    }

                    class Password(
                        value: String,
                        isPristine: Boolean = true
                    ) : FormInput<String, PasswordError>(value, isPristine) {
                        override fun validator(value: String): PasswordError? = when {
                            value.isBlank() -> PasswordError.Empty
                            value.length < 8 -> PasswordError.TooShort
                            else -> null
                        }
                    }
                    
                    //From State
                    data class LoginFormState(
                        val email: Email = Email(""),
                        val password: Password = Password(""),
                        val submissionStatus: FormSubmissionStatus = FormSubmissionStatus.Initial
                    ) : Form {
                        override val inputs = listOf(email, password)
                    }
                    
                    //ViewModel
                    class LoginViewModel(val authRepository: AuthRepository) : ViewModel() {

                        private val _state = MutableStateFlow(LoginFormState())
                        val state: StateFlow<LoginFormState> = _state

                        fun updateEmail(email: String) {
                            viewModelScope.launch {
                                _state.update { it.copy(email = Email(email, false)) }
                            }
                        }

                        fun updatePassword(password: String) {
                            viewModelScope.launch {
                                _state.update { it.copy(password = Password(password, false)) }
                            }
                        }

                        fun login() {
                            viewModelScope.launch {
                                val formState = _state.value
                                if (formState.isNotValid) return@launch

                                _state.update { it.copy(submissionStatus = FormSubmissionStatus.InProgress) }

                                authRepository.signInWithEmailAndPassword(
                                    email = formState.email.value,
                                    password = formState.password.value
                                ).onLeft { failure ->
                                    _state.update { it.copy(submissionStatus = FormSubmissionStatus.Failure) }
                                }.onRight {
                                    _state.update { it.copy(submissionStatus = FormSubmissionStatus.Success) }
                                }
                            }
                        }
                    }
                    
                    @Composable
                    private fun LoginFormFields(
                        email: Email,
                        onEmailChange: (String) -> Unit,
                        password: Password,
                        onPasswordChange: (String) -> Unit,
                        modifier: Modifier = Modifier,
                        formEnabled: Boolean = true
                    ) {
                        Column(
                            modifier = modifier,
                            verticalArrangement = Arrangement.spacedBy(MaterialTheme.spacing.large)
                        ) {
                            SimpleTextField(
                                label = "Email",
                                text = email.value,
                                onChange = onEmailChange,
                                error = email.displayError?.let {
                                    when (it) {
                                        EmailError.Empty -> "Email is required"
                                        EmailError.Invalid -> "Invalid email address"
                                    }
                                },
                                enabled = formEnabled
                            )

                            PasswordTextField(
                                label = "Password",
                                text = password.value,
                                onChange = onPasswordChange,
                                error = password.displayError?.let {
                                    when (it) {
                                        PasswordError.Empty -> "Password is required"
                                        PasswordError.TooShort -> "Password must be at least 8 characters long"
                                    }
                                },
                                enabled = formEnabled
                            )
                        }
                    }
                    
                    @Composable
                    fun LoginScreen(
                        viewModel: LoginViewModel = hiltViewModel(),
                    ) {
                        val formState by viewModel.state.collectAsStateWithLifecycle()

                        LoginFormFields(
                            email: formState.email,
                            onEmailChange = viewModel::updateEmail,
                            password: formState.password,
                            onPasswordChange = viewModel::updatePassword,
                            formEnabled = !formState.submissionStatus.isInProgressOrSuccess
                        )
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://github.com/zainulhassan815/rc3-food-delivery/tree/main/app/src/main/java/org/dreamerslab/rc3/utils/formfields
//https://zainulhassan815.medium.com/formz-kotlin-a-multiplatform-form-validation-library-3af42d4f58e9